import React, { useState, useEffect } from 'react';
import api from './api';
import { Spinner, ErrorBox } from './components';

function AddUserDialog({ onClose, onAdded }) {
  const [username, setU] = useState('');
  const [password, setP] = useState('');
  const [role, setR]     = useState('viewer');
  const [err, setErr]    = useState('');

  const submit = async () => {
    if (!username || !password) { setErr('All fields required'); return; }
    try {
      await api.post('/users', { username, password, role });
      onAdded();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to create user');
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-box" onClick={e => e.stopPropagation()}>
        <div className="dialog-title">CREATE USER</div>
        {err && <div style={{ color: 'var(--danger)', fontFamily: 'var(--mono)', fontSize: 11, marginBottom: 14 }}>[ {err} ]</div>}
        <div className="dialog-field">
          <label className="dialog-label">USERNAME</label>
          <input className="inp" value={username} onChange={e => setU(e.target.value)} autoFocus />
        </div>
        <div className="dialog-field">
          <label className="dialog-label">PASSWORD</label>
          <input className="inp" type="password" value={password} onChange={e => setP(e.target.value)} />
        </div>
        <div className="dialog-field">
          <label className="dialog-label">ROLE</label>
          <select className="inp" value={role} onChange={e => setR(e.target.value)}>
            <option value="viewer">viewer</option>
            <option value="admin">admin</option>
          </select>
        </div>
        <div className="dialog-actions">
          <button className="btn primary" onClick={submit}>CREATE</button>
          <button className="btn" onClick={onClose}>CANCEL</button>
        </div>
      </div>
    </div>
  );
}

function ChangePasswordDialog({ user, onClose }) {
  const [pw, setPw]   = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState('');
  const [ok, setOk]   = useState(false);

  const submit = async () => {
    if (!pw) { setErr('Password required'); return; }
    if (pw !== pw2) { setErr('Passwords do not match'); return; }
    try {
      await api.put(`/users/${user.id}/password`, { password: pw });
      setOk(true);
      setTimeout(onClose, 1200);
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed');
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-box" onClick={e => e.stopPropagation()}>
        <div className="dialog-title">CHANGE PASSWORD – {user.username}</div>
        {err && <div style={{ color: 'var(--danger)', fontFamily: 'var(--mono)', fontSize: 11, marginBottom: 14 }}>[ {err} ]</div>}
        {ok  && <div style={{ color: 'var(--ok)',    fontFamily: 'var(--mono)', fontSize: 11, marginBottom: 14 }}>[ Password updated ]</div>}
        <div className="dialog-field">
          <label className="dialog-label">NEW PASSWORD</label>
          <input className="inp" type="password" value={pw} onChange={e => setPw(e.target.value)} autoFocus />
        </div>
        <div className="dialog-field">
          <label className="dialog-label">CONFIRM</label>
          <input className="inp" type="password" value={pw2} onChange={e => setPw2(e.target.value)} />
        </div>
        <div className="dialog-actions">
          <button className="btn primary" onClick={submit}>SAVE</button>
          <button className="btn" onClick={onClose}>CANCEL</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [pwUser, setPwUser]   = useState(null);

  const load = async () => {
    try {
      const r = await api.get('/users');
      setUsers(r.data);
    } catch (e) {
      setError('Failed to load users');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const disableUser = async (u) => {
    if (!window.confirm(`Disable ${u.username}? They will not be able to log in.`)) return;
    try {
      await api.patch(`/users/${u.id}/active`, { active: false });
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed');
    }
  };

  const enableUser = async (u) => {
    try {
      await api.patch(`/users/${u.id}/active`, { active: true });
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed');
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(
      `Permanently delete ${u.username}?\n\nThis cannot be undone. Their account and all sessions will be removed.`
    )) return;
    try {
      await api.delete(`/users/${u.id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed');
    }
  };

  return (
    <div className="admin-wrap">
      <div className="overview-toolbar">
        <h1>USER MANAGEMENT</h1>
        <button className="btn primary" onClick={() => setShowAdd(true)}>+ CREATE USER</button>
      </div>

      {loading && <Spinner />}
      {error   && <ErrorBox msg={error} />}

      <div className="card">
        <table className="tbl">
          <thead>
            <tr>
              <th>ID</th>
              <th>USERNAME</th>
              <th>ROLE</th>
              <th>STATUS</th>
              <th>CREATED</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} style={{ opacity: u.active ? 1 : 0.55 }}>
                <td className="mono muted">{u.id}</td>
                <td className="mono">{u.username}</td>
                <td className="mono">
                  <span style={{ color: u.role === 'admin' ? 'var(--accent)' : 'var(--muted)' }}>
                    {u.role}
                  </span>
                </td>
                <td className="mono">
                  <span style={{ color: u.active ? 'var(--ok)' : 'var(--danger)' }}>
                    {u.active ? '● active' : '○ disabled'}
                  </span>
                </td>
                <td className="mono muted" style={{ fontSize: 11 }}>
                  {new Date(u.created_at).toLocaleDateString()}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button className="btn" style={{ fontSize: 11, padding: '3px 8px' }}
                            onClick={() => setPwUser(u)}>
                      PASSWD
                    </button>
                    {u.active ? (
                      <button className="btn" style={{ fontSize: 11, padding: '3px 8px',
                                borderColor: 'var(--warn)', color: 'var(--warn)' }}
                              onClick={() => disableUser(u)}>
                        DISABLE
                      </button>
                    ) : (
                      <button className="btn" style={{ fontSize: 11, padding: '3px 8px',
                                borderColor: 'var(--ok)', color: 'var(--ok)' }}
                              onClick={() => enableUser(u)}>
                        ENABLE
                      </button>
                    )}
                    <button className="btn danger" style={{ fontSize: 11, padding: '3px 8px' }}
                            onClick={() => deleteUser(u)}>
                      DELETE
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && <AddUserDialog onClose={() => setShowAdd(false)} onAdded={load} />}
      {pwUser  && <ChangePasswordDialog user={pwUser} onClose={() => { setPwUser(null); }} />}
    </div>
  );
}
